/**
 * Season Phases Service
 *
 * Handles season phase configuration, automatic phase detection,
 * admin overrides, and nutrition profiles per phase.
 */

import { HTTP } from "./http";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER = "http://www.localhost:8888/back-end-endpoint-season-phases.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-season-phases.php";
}

const SeasonPhases = {
  /**
   * Get current season phase for club/team
   * @param {number} clubId - Club ID
   * @param {number|null} teamId - Optional team ID for team-specific overrides
   */
  getCurrentPhase(clubId, teamId = null) {
    return HTTP.post(API_SERVER, {
      action: "getCurrentPhase",
      club_id: clubId,
      team_id: teamId,
    });
  },

  /**
   * Get full phase configuration for a club
   * @param {number} clubId - Club ID
   */
  getPhaseConfig(clubId) {
    return HTTP.post(API_SERVER, {
      action: "getPhaseConfig",
      club_id: clubId,
    });
  },

  /**
   * List every season the club has data for (newest first), for the
   * global season picker. Each item: { season_year, label, start_date,
   * end_date, is_current, source }.
   * @param {string} clubId - Club ID (tenant string e.g. "ptfc-2085XH")
   */
  listSeasons(clubId) {
    return HTTP.post(API_SERVER, {
      action: "listSeasons",
      club_id: clubId,
    });
  },

  /**
   * Get next season configuration status (whether next year's row exists)
   * @param {number} clubId - Club ID
   */
  getNextSeasonStatus(clubId) {
    return HTTP.post(API_SERVER, {
      action: "getNextSeasonStatus",
      club_id: clubId,
    });
  },

  /**
   * Roll forward to next season — creates next year's season_phases row
   * by copying the current year with dates shifted +1 year. Idempotent.
   * @param {number} clubId - Club ID
   */
  rollForwardSeason(clubId) {
    return HTTP.post(API_SERVER, {
      action: "rollForwardSeason",
      club_id: clubId,
    });
  },

  /**
   * Get nutrition profile for current phase
   * @param {number} clubId - Club ID
   * @param {number|null} teamId - Optional team ID
   */
  getNutritionProfile(clubId, teamId = null) {
    return HTTP.post(API_SERVER, {
      action: "getNutritionProfile",
      club_id: clubId,
      team_id: teamId,
    });
  },

  /**
   * Get complete nutrition context including phase, match day, and calculated targets
   * @param {number} clubId - Club ID
   * @param {number|null} teamId - Optional team ID
   * @param {number|null} playerWeight - Optional player weight in kg for target calculations
   */
  getNutritionContext(clubId, teamId = null, playerWeight = null) {
    return HTTP.post(API_SERVER, {
      action: "getNutritionContext",
      club_id: clubId,
      team_id: teamId,
      player_weight: playerWeight,
    });
  },

  /**
   * Get match day specific nutrition guidelines
   * @param {number} clubId - Club ID
   * @param {number} dayRelative - Days relative to match (-3 to +3)
   */
  getMatchDayNutrition(clubId, dayRelative = 0) {
    return HTTP.post(API_SERVER, {
      action: "getMatchDayNutrition",
      club_id: clubId,
      day_relative: dayRelative,
    });
  },

  /**
   * Get all match day nutrition profiles
   * @param {number} clubId - Club ID
   */
  getAllMatchDayNutrition(clubId) {
    return HTTP.post(API_SERVER, {
      action: "getAllMatchDayNutrition",
      club_id: clubId,
    });
  },

  /**
   * Get days to next match for a team
   * @param {number} teamId - Team ID
   * @param {string|null} targetDate - Optional target date (YYYY-MM-DD)
   */
  getDaysToMatch(teamId, targetDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getDaysToMatch",
      team_id: teamId,
      target_date: targetDate,
    });
  },

  /**
   * Get next activity (training or match) - whichever is closest
   * Returns both next training and next match, plus which is the immediate next activity
   * @param {number} teamId - Team ID
   * @param {string|null} targetDate - Optional target date (YYYY-MM-DD)
   */
  getNextActivity(teamId, targetDate = null) {
    return HTTP.post(API_SERVER, {
      action: "getNextActivity",
      team_id: teamId,
      target_date: targetDate,
    });
  },

  /**
   * Set admin phase override for club
   * @param {number} clubId - Club ID
   * @param {string} phase - Phase to override to (preseason|regular|postseason|offseason)
   * @param {string|null} expires - Optional expiry date (YYYY-MM-DD)
   * @param {string|null} reason - Optional reason for override
   */
  setPhaseOverride(clubId, phase, expires = null, reason = null) {
    return HTTP.post(API_SERVER, {
      action: "setPhaseOverride",
      club_id: clubId,
      phase: phase,
      expires: expires,
      reason: reason,
    });
  },

  /**
   * Clear phase override for club
   * @param {number} clubId - Club ID
   */
  clearPhaseOverride(clubId) {
    return HTTP.post(API_SERVER, {
      action: "clearPhaseOverride",
      club_id: clubId,
    });
  },

  /**
   * Save phase configuration
   * @param {number} clubId - Club ID
   * @param {object} config - Phase configuration object
   */
  savePhaseConfig(clubId, config) {
    return HTTP.post(API_SERVER, {
      action: "savePhaseConfig",
      club_id: clubId,
      config: config,
    });
  },

  /**
   * Set team-specific phase override
   * @param {number} teamId - Team ID
   * @param {string} phase - Phase to override to
   * @param {string} startDate - Override start date (YYYY-MM-DD)
   * @param {string} endDate - Override end date (YYYY-MM-DD)
   * @param {string|null} reason - Optional reason
   * @param {number} createdBy - User ID who created the override
   */
  setTeamPhaseOverride(teamId, phase, startDate, endDate, reason, createdBy) {
    return HTTP.post(API_SERVER, {
      action: "setTeamPhaseOverride",
      team_id: teamId,
      phase: phase,
      start_date: startDate,
      end_date: endDate,
      reason: reason,
      created_by: createdBy,
    });
  },

  /**
   * Get all phase overrides for a team
   * @param {number} teamId - Team ID
   */
  getTeamPhaseOverrides(teamId) {
    return HTTP.post(API_SERVER, {
      action: "getTeamPhaseOverrides",
      team_id: teamId,
    });
  },

  /**
   * Delete a team phase override
   * @param {number} overrideId - Override ID to delete
   */
  deleteTeamPhaseOverride(overrideId) {
    return HTTP.post(API_SERVER, {
      action: "deleteTeamPhaseOverride",
      override_id: overrideId,
    });
  },

  // ===========================================================================
  // UTILITY METHODS (client-side)
  // ===========================================================================

  /**
   * Get display info for a phase (labels, colors, icons)
   * @param {string} phase - Phase name
   */
  getPhaseDisplayInfo(phase) {
    const phaseInfo = {
      preseason: {
        id: "preseason",
        label: "Pre-Season",
        shortLabel: "Pre",
        color: "#28a745",
        bgColor: "#d4edda",
        borderColor: "#c3e6cb",
        icon: "running",
        emoji: "🏃",
        description: "Fitness building & team preparation",
        focus: "Build muscle mass, optimize body composition",
      },
      regular: {
        id: "regular",
        label: "Regular Season",
        shortLabel: "Season",
        color: "#007bff",
        bgColor: "#cce5ff",
        borderColor: "#b8daff",
        icon: "futbol",
        emoji: "⚽",
        description: "Competitive matches & recovery cycles",
        focus: "Maintain performance, support recovery",
      },
      postseason: {
        id: "postseason",
        label: "Playoffs",
        shortLabel: "Playoffs",
        color: "#fd7e14",
        bgColor: "#ffe5d0",
        borderColor: "#ffd8b8",
        icon: "trophy",
        emoji: "🏆",
        description: "Peak performance for knockout rounds",
        focus: "Peak performance, maximize recovery",
      },
      offseason: {
        id: "offseason",
        label: "Off-Season",
        shortLabel: "Off",
        color: "#6c757d",
        bgColor: "#e9ecef",
        borderColor: "#dee2e6",
        icon: "umbrella-beach",
        emoji: "🌴",
        description: "Rest & recovery period",
        focus: "Maintain muscle, prevent weight gain",
      },
    };
    return phaseInfo[phase] || phaseInfo.offseason;
  },

  /**
   * Get all phases for dropdown/selection
   */
  getAllPhases() {
    return [
      this.getPhaseDisplayInfo("preseason"),
      this.getPhaseDisplayInfo("regular"),
      this.getPhaseDisplayInfo("postseason"),
      this.getPhaseDisplayInfo("offseason"),
    ];
  },

  /**
   * Format date for database (YYYY-MM-DD)
   * @param {Date|string} date - Date to format
   */
  formatDateForDb(date) {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  },

  /**
   * Format date for display (DD/MM/YYYY)
   * @param {string} date - Date string in YYYY-MM-DD format
   */
  formatDateForDisplay(date) {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB");
  },

  /**
   * Calculate nutrition targets for a player weight
   * @param {object} profile - Nutrition profile with min/max values in g/kg
   * @param {number} weight - Player weight in kg
   */
  calculateTargets(profile, weight) {
    if (!profile || !weight || weight <= 0) return null;

    return {
      carbs: {
        min: Math.round(profile.carb_min * weight),
        max: Math.round(profile.carb_max * weight),
        unit: "g",
      },
      protein: {
        min: Math.round(profile.protein_min * weight),
        max: Math.round(profile.protein_max * weight),
        unit: "g",
      },
      fat: {
        min: Math.round(profile.fat_min * weight),
        max: Math.round(profile.fat_max * weight),
        unit: "g",
      },
      hydration: {
        target: Math.round(profile.hydration_target * weight),
        unit: "mL",
      },
    };
  },

  /**
   * Get match day label from days relative
   * @param {number} days - Days relative to match
   */
  getMatchDayLabel(days) {
    if (days === 0) return "Match Day";
    if (days < 0) return `MD+${Math.abs(days)}`;
    return `MD-${days}`;
  },

  /**
   * Check if a date is within a phase
   * @param {string} date - Date to check (YYYY-MM-DD)
   * @param {object} phaseConfig - Phase configuration with start/end dates
   */
  isDateInPhase(date, phaseConfig) {
    const checkDate = new Date(date);
    const phases = ["preseason", "regular", "postseason", "offseason"];

    for (const phase of phases) {
      const startKey = `${phase === "regular" ? "regular_season" : phase}_start`;
      const endKey = `${phase === "regular" ? "regular_season" : phase}_end`;

      if (phaseConfig[startKey] && phaseConfig[endKey]) {
        const start = new Date(phaseConfig[startKey]);
        const end = new Date(phaseConfig[endKey]);

        if (checkDate >= start && checkDate <= end) {
          return phase;
        }
      }
    }

    return "offseason";
  },
};

export { SeasonPhases };
export default SeasonPhases;
